<!--
  @component

  The rounded button component

  ```html
  <oc-rounded-button-v1 elevated disabled icon-type="arrow-right" size="200" />
  ```
-->
<svelte:options
  customElement={{
    tag: "oc-rounded-button-v1",
    props: {
      elevated: { type: "Boolean" },
      transparent: { type: "Boolean" },
      active: { type: "Boolean", reflect: true },
      disabled: { type: "Boolean", reflect: true },
      iconType: { type: "String", attribute: "icon-type" },
      size: { type: "String", reflect: true },
    },
  }}
/>

<script lang="ts">
  import type { IconType } from "@otto-ec/icons";

  /**
   * Enable / disable elevation (not working for size 100 or transparent button)
   * @default false
   */
  export let elevated = false;
  /**
   * Makes the button background transparent
   * @default false
   */
  export let transparent = false;
  /**
   * Disable / enable the button
   * @default false
   */
  export let disabled = false;
  /**
   * Set the used icon type
   */
  export let iconType: IconType = "wishlist";
  /**
   * Size of the button
   * @default "50"
   */
  export let size: "50" | "100" = "50";
</script>

<button
  type="button"
  class="rounded-button {`rounded-button--size-${size}`}"
  {disabled}
  class:rounded-button--transparent={transparent}
  class:rounded-button--disabled={disabled}
  class:rounded-button--elevated={size === "50" && !transparent ? elevated : false}
  tabindex="0"
>
  <oc-icon-v1
    class="rounded-button__icon"
    class:rounded-button__icon--disabled={disabled}
    type={iconType}
    size={"100"}
  />
</button>

<style lang="scss">
  @use "@otto-ec/design-tokens/tokens";
  @use "./RoundedButtonV1.config" as config;

  :host {
    @include config.oc-rounded-button;
  }

  :host([size="50"]) {
    @include config.oc-rounded-button-size-50;
  }

  :host([size="100"]) {
    @include config.oc-rounded-button-size-100;
  }

  // default is size 50
  .rounded-button {
    position: relative;
    background-color: tokens.$oc-component-rounded-button-default-background-color;
    border-radius: 50%;
    border: none;
    height: 32px;
    width: 32px;

    // enhances the clickable area for button of size 50
    &:after {
      content: "";
      position: absolute;
      width: 48px;
      height: 48px;
      top: -8px;
      left: -8px;
      background-color: transparent;
    }

    // size 100 makes background area bigger
    &--size-100 {
      height: 48px;
      width: 48px;
    }

    &:hover:enabled {
      cursor: pointer;
      background-color: tokens.$oc-component-rounded-button-default-background-color-hover;
    }

    &:active:enabled {
      cursor: pointer;
      background-color: tokens.$oc-component-rounded-button-default-background-color-active;
    }

    &:focus:enabled {
      outline: 2px solid tokens.$oc-component-rounded-button-focus-color;
    }

    &--transparent {
      background-color: tokens.$oc-component-rounded-button-transparent-background-color;

      &:hover:enabled {
        background-color: tokens.$oc-component-rounded-button-transparent-background-color-hover;
      }
      &:active:enabled {
        background-color: tokens.$oc-component-rounded-button-transparent-background-color-active;
      }
    }

    &--elevated {
      &:not(.rounded-button--transparent) {
        // old shadow, new coming soon + design token
        filter: drop-shadow(0px 1px 2px rgba(0, 0, 0, 0.3));
        box-shadow: 0 1px 3px 1px rgb(0 0 0 / 15%);
      }
    }

    &__icon {
      fill: #222222;
      left: 50%;
      top: 50%;
      position: absolute;
      transform: translate(-50%, -50%);
      &--disabled {
        fill: tokens.$oc-component-rounded-button-icon-color-disabled;
      }
    }
  }
</style>
